import { Tool, Web3DViewer } from '@technology/web3d';
import {
  CADEventName,
  LineEditingModel,
  LineEditingPlugin,
} from '@technology/web3d-plugin-line-editing';
import { DrawingTool, NO_ACTIVE_TOOL } from '@trimble/geospatial';
import { ILogger } from '@trimble/logging';
import {
  Accessor,
  batch,
  createEffect,
  createSignal,
  on,
  onCleanup,
  untrack,
} from 'solid-js';
import type { CadToolInteractionPane } from './cad-drawing-tools.types';
import { drawingStyleForTool } from './feature-extraction-cad-config';

export interface CreateCadDrawingToolsParams {
  readonly viewer: Web3DViewer;
  readonly lineEditingPlugin: LineEditingPlugin;
  readonly model: LineEditingModel;
  readonly logger: ILogger;
  readonly interactionPane?: CadToolInteractionPane;
}

export interface CadDrawingTools {
  readonly activeTool: Accessor<DrawingTool | undefined>;
  toggleTool(tool: DrawingTool): void;
  cancelActiveTool(): void;
  dispose(): void;
}

export function createCadDrawingTools(
  params: CreateCadDrawingToolsParams
): CadDrawingTools {
  const [activeTool, setActiveTool] = createSignal<DrawingTool | undefined>(
    undefined
  );
  // Prefer tool.enabled over viewer.activeTool (matches markup-capability).
  const getRegisteredTool = (name: string): Tool | undefined =>
    params.viewer.tools[name] as Tool | undefined;
  let heldOffTool: Tool | undefined;

  const resetPaneHold = (): void => {
    heldOffTool = undefined;
    params.interactionPane?.unlockToolPane();
  };

  const toggleTool = (tool: DrawingTool): void => batch(() => switchTool(tool));

  const switchTool = (tool: DrawingTool): void => {
    if (activeTool() === tool) {
      cancelActiveTool();
      return;
    }
    params.lineEditingPlugin.setActiveModel(params.model);
    params.model.setDrawingStyle(drawingStyleForTool(tool));
    const nextTool = getRegisteredTool(tool);
    if (!nextTool) {
      params.logger.error(
        'Failed to activate line editing tool; it is not registered on the viewer toolManager',
        { tool }
      );
      return;
    }
    try {
      const previousTool = activeTool();
      const previousToolInstance = getRegisteredTool(
        previousTool ?? NO_ACTIVE_TOOL
      );
      if (previousToolInstance) {
        previousToolInstance.enabled = false;
      }
      resetPaneHold();
      nextTool.enabled = true;
      setActiveTool(tool);
      params.logger.info('Activated line editing tool', { tool });
    } catch (error) {
      params.logger.error(
        'Failed to activate line editing tool',
        { tool },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  };

  const cancelActiveTool = (): void => batch(cancelTool);

  const cancelTool = (): void => {
    const previousTool = activeTool();
    if (previousTool === undefined) {
      return;
    }
    try {
      const previousToolInstance = getRegisteredTool(previousTool);
      const wasOwningInteraction =
        (previousToolInstance?.enabled ?? false) ||
        (previousToolInstance !== undefined &&
          previousToolInstance === heldOffTool);
      resetPaneHold();
      if (previousToolInstance) {
        previousToolInstance.enabled = false;
      }
      if (wasOwningInteraction) {
        const selectionTool = getRegisteredTool(NO_ACTIVE_TOOL);
        if (selectionTool) {
          selectionTool.enabled = true;
        }
      }
    } catch (error) {
      params.logger.error(
        'Failed to deactivate line editing tool',
        { defaultTool: NO_ACTIVE_TOOL },
        error instanceof Error ? error : new Error(String(error))
      );
    }
    setActiveTool(undefined);
    params.logger.info('Cancelled line editing tool');
  };

  if (params.interactionPane) {
    holdToolOutsideToolPane(params.interactionPane);
  }

  // Snapping CAD tools pick on every window mouse move, so only the tool pane keeps one on.
  // Turning a CAD tool off discards the line being drawn, so a press locks the tool pane
  // until the line is committed.
  function holdToolOutsideToolPane(pane: CadToolInteractionPane): void {
    createEffect(
      on(
        pane.presses,
        () => {
          if (activeTool() !== undefined) {
            pane.lockToolPane();
          }
        },
        { defer: true }
      )
    );

    const onCommit = (): void => pane.unlockToolPane();
    params.viewer.addEventListener(CADEventName.ActionPerformed, onCommit);
    onCleanup(() =>
      params.viewer.removeEventListener(CADEventName.ActionPerformed, onCommit)
    );

    createEffect(() => {
      const tool = activeTool();
      const isToolPane = pane.isToolPane();
      const instance = tool === undefined ? undefined : getRegisteredTool(tool);
      if (!instance) {
        return;
      }
      untrack(() => {
        if (isToolPane && heldOffTool === instance) {
          heldOffTool = undefined;
          instance.enabled = true;
        } else if (
          !isToolPane &&
          heldOffTool !== instance &&
          instance.enabled
        ) {
          heldOffTool = instance;
          instance.enabled = false;
        }
      });
    });
  }

  return {
    activeTool,
    toggleTool,
    cancelActiveTool,
    dispose: cancelActiveTool,
  };
}
